import { useState, useEffect, useRef } from "react";
import { getDomainName } from "./HelperFunctions";
import { ErrorCode, EventType, QueryParam } from "./Constants";
import {
  CONFIG_FETCH_TIMEOUT_MS,
  FetchError,
  fetchWithTimeout,
  looksBlocked,
} from "./FetchError";
import postEvent, { createBaseLanderEvent } from "./LanderEvent";
import { Settings } from "../config";
import { isProxyPath, toProxyPath } from "./proxyMode";

export const useFetch = (
  url: string,
  queryConfig: Record<string, any> = {},
  extraHeaders: Record<string, string> = {}
) => {
  const [body, setBody] = useState<any>(null);
  const [err, setErr] = useState<FetchError | null>(null);
  const eventFiredRef = useRef(false);

  const fireErrorEvent = (errorCode: string) => {
    if (eventFiredRef.current) return;
    eventFiredRef.current = true;

    const event = createBaseLanderEvent(EventType.ERROR, {});
    event.errorCode = errorCode;
    event.sToken = queryConfig[QueryParam.S_TOKEN];
    event.domain = getDomainName(queryConfig) || window.location.hostname;
    postEvent(Settings.EVENT_PUBLISH_API, event);
  };

  // Records that the direct (absolute-host) call was blocked and we retried via
  // the same-origin proxy. Sent through the proxy events path so it's delivered
  // even when the absolute host is blocked for this visitor.
  const fireProxyFallbackEvent = (directErrorCode: string) => {
    const event = createBaseLanderEvent(EventType.PROXY_FALLBACK, {});
    event.errorCode = directErrorCode;
    event.sToken = queryConfig[QueryParam.S_TOKEN];
    event.domain = getDomainName(queryConfig) || window.location.hostname;
    postEvent(toProxyPath(Settings.EVENT_PUBLISH_API), event);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let { response, error } = await fetchWithTimeout(
        url,
        CONFIG_FETCH_TIMEOUT_MS,
        extraHeaders
      );

      // If the direct (absolute-host) call looks blocked, retry via the
      // same-origin proxy. The page already loaded from this domain, so the
      // /api/ path is reachable even when the cross-origin host is blocked.
      // extraHeaders must ride the retry too, or the header (e.g. X-Visitor-Id)
      // is lost on exactly the blocked/CN path where the proxy is used.
      if (error && looksBlocked(error) && !isProxyPath(url)) {
        const proxyUrl = toProxyPath(url);
        if (proxyUrl !== url) {
          fireProxyFallbackEvent(error.errorCode);
          ({ response, error } = await fetchWithTimeout(
            proxyUrl,
            CONFIG_FETCH_TIMEOUT_MS,
            extraHeaders
          ));
        }
      }

      if (cancelled) return;

      if (error) {
        setErr(error);
        fireErrorEvent(error.errorCode);
        return;
      }

      if (response) {
        try {
          setBody(await response.json());
        } catch {
          const parseError = new FetchError(
            "Failed to parse response JSON",
            ErrorCode.CONFIG_FETCH_INVALID_JSON
          );
          setErr(parseError);
          fireErrorEvent(parseError.errorCode);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // queryConfig and extraHeaders intentionally omitted - they're stable for a
    // given page and must NOT be effect deps: keeping the visitor id out of the
    // dep set is what prevents the PARKING-5187 render->fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return {
    body,
    err,
  };
};
